import {
	Button,
	CircularProgress,
	IconButton,
	InputAdornment,
	Stack,
	TextField,
	type SxProps,
	type Theme,
} from "@mui/material";
import { Clear, Search } from "@mui/icons-material";
import { AsyncStatus } from "@gui/widgets/A11y";
import { useJobLookupViewModel, type JobLookupProps } from "./JobLookupField.vm";

/** How tall the box stands: MUI's small outlined field. The button beside it takes the same, and
 *  so should anything else that shares a header row with it — a row of controls at three
 *  heights reads as three rows. */
export const FIELD_HEIGHT = 40;

interface JobLookupFieldProps extends JobLookupProps {
	/** What the button says; "Load" unless told otherwise. */
	submitLabel?: string;
	/** Drop the button: Enter alone looks the job up, and a spinner in the box shows it working. */
	hideSubmit?: boolean;
	/** Contained when the lookup is the page's main act, outlined when it is a side door. */
	variant?: "contained" | "outlined";
	/** The box takes the row's width instead of a fixed one. */
	fullWidth?: boolean;
	/** Button under the box rather than beside it, for a phone. */
	stacked?: boolean;
	disabled?: boolean;
	autoFocus?: boolean;
	sx?: SxProps<Theme>;
}

/** One box for finding a job by whatever is to hand — its record id, its Airtable link, or its
 *  name — shared by the pages that take a job the operator names rather than one a list offers.
 *  The X clears; the button, or Enter, looks it up; a miss is said under the box. */
export const JobLookupField: React.FC<JobLookupFieldProps> = ({
	submitLabel = "Load",
	hideSubmit = false,
	variant = "contained",
	fullWidth = false,
	stacked = false,
	disabled = false,
	autoFocus = false,
	sx,
	...lookup
}) => {
	const vm = useJobLookupViewModel(lookup);
	return (
		<>
			<AsyncStatus state={vm.loading ? "loading" : "ready"} loadingText="Looking up the job…" />
			<Stack
				direction={stacked ? "column" : "row"}
				spacing={1}
				alignItems={stacked ? "stretch" : "flex-start"}
				component="form"
				onSubmit={vm.submit}
				sx={sx}
			>
				<TextField
					size="small"
					placeholder="Record id, Airtable link, or job name"
					value={vm.value}
					onChange={(event) => vm.edit(event.target.value)}
					error={vm.error !== null}
					helperText={vm.error}
					disabled={disabled || vm.loading}
					autoFocus={autoFocus}
					inputProps={{ "aria-label": "Job record id, Airtable link, or name", spellCheck: false }}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Search fontSize="small" />
							</InputAdornment>
						),
						endAdornment:
							hideSubmit && vm.loading ? (
								<InputAdornment position="end">
									<CircularProgress size={16} color="inherit" />
								</InputAdornment>
							) : vm.value ? (
								<InputAdornment position="end">
									<IconButton size="small" onClick={vm.clear} aria-label="Clear" disabled={disabled || vm.loading}>
										<Clear fontSize="small" />
									</IconButton>
								</InputAdornment>
							) : undefined,
					}}
					sx={fullWidth || stacked ? { flex: 1 } : { width: 420 }}
				/>
				{/* Without the button, the form still submits on Enter: one text field is enough for that. */}
				{!hideSubmit && (
					<Button
						type="submit"
						variant={variant}
						startIcon={vm.loading ? <CircularProgress size={16} color="inherit" /> : undefined}
						disabled={disabled || !vm.canSubmit}
						sx={{ whiteSpace: "nowrap", height: FIELD_HEIGHT, flexShrink: 0 }}
					>
						{submitLabel}
					</Button>
				)}
			</Stack>
		</>
	);
};
